import { supabase } from '../lib/supabase';

import OpenAI from 'openai';

function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const chatbotService = {
  /**
   * Get chatbot response with conversation history
   */
  async getChatbotResponse(userMessage, conversationId, userId, tenantId, contextData = {}) {
    try {
      // Load conversation history
      const history = await this.getConversationHistory(conversationId, userId, tenantId);

      // Build messages array with history
      const messages = [
        {
          role: 'system',
          content: `You are an AI Fitness Assistant for a gym. You provide:
- Personalized workout guidance and modifications
- Nutrition advice and meal planning
- Exercise form corrections and injury prevention tips
- Motivational coaching and progress tracking
- Technical support for gym services
- Billing and membership inquiries
- General fitness questions

Be friendly, professional, and supportive. Provide actionable advice tailored to the member's fitness level and goals. If asked about billing or technical issues, provide helpful guidance or direct them to staff if needed.`
        },
        ...history?.map(msg => ({
          role: msg?.role,
          content: msg?.content
        })),
        {
          role: 'user',
          content: userMessage
        }
      ];

      // Get OpenAI response
      const openaiClient = new OpenAI({
        apiKey: import.meta.env.VITE_OPENAI_API_KEY,
        dangerouslyAllowBrowser: true,
      });

      const response = await openaiClient?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: messages,
        max_completion_tokens: 1000,
        reasoning_effort: 'minimal',verbosity: 'medium'
      });

      const aiResponse = response?.choices?.[0]?.message?.content;

      // Save conversation messages
      await this.saveConversationMessage(conversationId, userId, tenantId, userMessage, 'user');
      await this.saveConversationMessage(conversationId, userId, tenantId, aiResponse, 'assistant');

      return {
        success: true,
        response: aiResponse,
        conversationId: conversationId
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error in chatbot response:', error);
      }
      return {
        success: false,
        error: errorInfo?.message
      };
    }
  },

  /**
   * Get conversation history
   */
  async getConversationHistory(conversationId, userId, tenantId) {
    try {
      if (!conversationId) return [];

      const { data, error } = await supabase
        ?.from('chatbot_conversations')
        ?.select('*')
        ?.eq('conversation_id', conversationId)
        ?.eq('user_id', userId)
        ?.eq('tenant_id', tenantId)
        ?.order('created_at', { ascending: true })
        ?.limit(20); // Last 20 messages for context

      if (error) throw error;
      return data || [];
    } catch (error) {
      console.error('Error loading conversation history:', error);
      return [];
    }
  },

  /**
   * Save conversation message
   */
  async saveConversationMessage(conversationId, userId, tenantId, content, role) {
    try {
      const { error } = await supabase
        ?.from('chatbot_conversations')
        ?.insert({
          conversation_id: conversationId,
          user_id: userId,
          tenant_id: tenantId,
          role: role,
          content: content,
          created_at: new Date()?.toISOString()
        });

      if (error) throw error;
    } catch (error) {
      console.error('Error saving conversation message:', error);
    }
  },

  /**
   * Create new conversation
   */
  async createConversation(userId, tenantId, title = 'New Conversation') {
    try {
      const conversationId = `conv_${Date.now()}_${Math.random()?.toString(36)?.substr(2, 9)}`;
      
      const { data, error } = await supabase
        ?.from('chatbot_conversation_metadata')
        ?.insert({
          conversation_id: conversationId,
          user_id: userId,
          tenant_id: tenantId,
          title: title,
          created_at: new Date()?.toISOString(),
          updated_at: new Date()?.toISOString()
        })
        ?.select()
        ?.single();

      if (error) throw error;
      return { success: true, conversationId: conversationId };
    } catch (error) {
      console.error('Error creating conversation:', error);
      return {
        success: false,
        error: error?.message || 'Failed to create conversation'
      };
    }
  },

  /**
   * Get user conversations
   */
  async getUserConversations(userId, tenantId) {
    try {
      const { data, error } = await supabase
        ?.from('chatbot_conversation_metadata')
        ?.select('*')
        ?.eq('user_id', userId)
        ?.eq('tenant_id', tenantId)
        ?.order('updated_at', { ascending: false })
        ?.limit(10);

      if (error) throw error;
      return { success: true, data: data || [] };
    } catch (error) {
      console.error('Error fetching conversations:', error);
      return {
        success: false,
        error: error?.message || 'Failed to fetch conversations'
      };
    }
  },

  /**
   * Delete conversation
   */
  async deleteConversation(conversationId, userId, tenantId) {
    try {
      // Delete conversation messages
      await supabase
        ?.from('chatbot_conversations')
        ?.delete()
        ?.eq('conversation_id', conversationId)
        ?.eq('user_id', userId)
        ?.eq('tenant_id', tenantId);

      // Delete conversation metadata
      const { error } = await supabase
        ?.from('chatbot_conversation_metadata')
        ?.delete()
        ?.eq('conversation_id', conversationId)
        ?.eq('user_id', userId)
        ?.eq('tenant_id', tenantId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error deleting conversation:', error);
      return {
        success: false,
        error: error?.message || 'Failed to delete conversation'
      };
    }
  },

  /**
   * Get quick action prompts
   */
  getQuickActionPrompts() {
    return [
      {
        category: 'Workout Guidance',
        prompts: [
          'Can you suggest a beginner-friendly workout routine?',
          'How do I modify exercises for a knee injury?',
          'What\'s the best way to improve my squat form?'
        ]
      },
      {
        category: 'Nutrition Advice',
        prompts: [
          'What should I eat before and after workouts?',
          'Can you help me create a meal plan for muscle gain?',
          'How much protein do I need daily?'
        ]
      },
      {
        category: 'Technical Support',
        prompts: [
          'How do I book a class?',
          'I need help with my membership billing',
          'How do I update my payment method?'
        ]
      },
      {
        category: 'Motivation',
        prompts: [
          'I\'m struggling to stay consistent with workouts',
          'How do I overcome a fitness plateau?',
          'Give me motivation to work out today'
        ]
      }
    ];
  }
};

export default chatbotService;